import { useApiClient, type ApiClient } from '@esnafai/shared/api';
import { useSyncExternalStore } from 'react';

// The server clock minus the browser clock, from the Date header of the GET /me answer (merchant screens §4.1 Clock).
// A skew over 60 s is used by the countdowns and shown once as a notice; a smaller one counts as none.
const SKEW_LIMIT_MS = 60_000;

export type ServerOffset = { offsetMs: number; skewed: boolean };

type Store = {
  offset: ServerOffset;
  listeners: Set<() => void>;
};

const noSkew: ServerOffset = { offsetMs: 0, skewed: false };
const stores = new WeakMap<ApiClient, Store>();

// One store per API client, fed by a middleware of that client (no fetch of its own). Once skewed, the notice flag
// stays for the session.
function storeOf(api: ApiClient): Store {
  const existing = stores.get(api);
  if (existing) return existing;
  const store: Store = { offset: noSkew, listeners: new Set() };
  stores.set(api, store);
  api.fetchClient.use({
    onResponse({ request, response }) {
      const date = response.headers.get('Date');
      if (!response.ok || !date || !new URL(request.url).pathname.endsWith('/api/me')) return;
      const skew = Date.parse(date) - Date.now();
      if (Number.isNaN(skew)) return;
      const offsetMs = Math.abs(skew) > SKEW_LIMIT_MS ? Math.round(skew / 1000) * 1000 : 0;
      const skewed = store.offset.skewed || offsetMs !== 0;
      if (offsetMs === store.offset.offsetMs && skewed === store.offset.skewed) return;
      store.offset = { offsetMs, skewed };
      for (const listener of store.listeners) listener();
    },
  });
  return store;
}

/** Watches a client from its creation, so the start-up GET /me, answered before any screen asks, is read too. */
export function watchServerClock(api: ApiClient): void {
  storeOf(api);
}

export function useServerOffset(): ServerOffset {
  const store = storeOf(useApiClient());
  return useSyncExternalStore(
    (listener) => {
      store.listeners.add(listener);
      return () => store.listeners.delete(listener);
    },
    () => store.offset,
  );
}
